import { ReactNode } from 'react';
import AccountCard from './AccountCard';
import SharedAccountBody, { formatMaybeDateTime } from './SharedAccountBody';
import { UserRetailAccount, RetailAccountContent, Position } from './types';

const normalizePercentRatio = (value?: number) => {
  if (value === undefined || value === null) return undefined;
  return value / 100;
};

export interface RetailAccountCardProps {
  account: UserRetailAccount;
  content?: RetailAccountContent;
  contentLoading: boolean;
  contentError?: string | null;
  onLoadContentIfNeeded: () => void;
  actions?: ReactNode;
  /** Optional row rendered inside the card, above Positions / Details. */
  controls?: ReactNode;
}

const RetailAccountCard = ({
  account,
  content,
  contentLoading,
  contentError,
  onLoadContentIfNeeded,
  actions,
  controls,
}: RetailAccountCardProps) => {
  const accountType = account.isPaper ? 'paper' : 'live';

  const title =
    account.nickname ||
    content?.accountName ||
    (account.isPaper ? 'Paper Trading' : 'Live Trading');

  const positions: Position[] = (content?.positions ?? []).map((p) => ({
    symbol: p.symbol,
    qty: p.qty,
    avgEntryPrice: p.avgEntryPrice,
    currentPrice: p.currentPrice,
    marketValue: p.marketValue,
    unrealizedPL: p.unrealizedPL,
    unrealizedPLPct: normalizePercentRatio(p.unrealizedPLPercentage),
  }));

  const performance = content?.performance
    ? content.performance
    : content?.profitSinceStart !== undefined
      ? {
          sinceStart: {
            amount: content.profitSinceStart ?? 0,
            percent: normalizePercentRatio(content.profitPercentage) ?? 0,
          },
        }
      : undefined;

  const trackRecord = (() => {
    const startIso = content?.trackRecordStartedAt;
    if (!startIso) return null;
    const start = new Date(startIso);
    const now = new Date();
    const totalDays = Math.floor((now.getTime() - start.getTime()) / 86_400_000);
    if (totalDays < 1) return 'Today';

    let months =
      (now.getFullYear() - start.getFullYear()) * 12 +
      (now.getMonth() - start.getMonth());
    let days = now.getDate() - start.getDate();
    if (days < 0) {
      months -= 1;
      days += new Date(now.getFullYear(), now.getMonth(), 0).getDate();
    }

    if (months <= 0) return `${days} day${days === 1 ? '' : 's'}`;
    const monthsPart = `${months} month${months === 1 ? '' : 's'}`;
    if (days <= 0) return monthsPart;
    return `${monthsPart} and ${days} day${days === 1 ? '' : 's'}`;
  })();

  const rowClass = 'grid grid-cols-[minmax(120px,140px)_1fr] items-baseline gap-x-4 gap-y-3';
  const dtClass =
    'm-0 text-[12px] font-semibold uppercase tracking-[0.04em] text-muted-foreground';
  const ddClass = 'm-0 break-words text-[13px] text-[color:var(--text-secondary)]';
  const monoClass = 'font-mono text-[12px]';

  const details = (
    <dl className="m-0 flex flex-col gap-2.5">
      <div className={rowClass}>
        <dt className={dtClass}>Identifier</dt>
        <dd className={`${ddClass} ${monoClass}`} title={account.userIdentifier || undefined}>
          {account.userIdentifier || 'N/A'}
        </dd>
      </div>
      <div className={rowClass}>
        <dt className={dtClass}>Endpoint</dt>
        <dd className={`${ddClass} ${monoClass} truncate`} title={account.endpoint}>
          {account.endpoint}
        </dd>
      </div>
      <div className={rowClass}>
        <dt className={dtClass}>Type</dt>
        <dd className={ddClass}>{account.isPaper ? 'Paper' : 'Live'}</dd>
      </div>
      {content?.trackRecordStartedAt && (
        <div className={rowClass}>
          <dt className={dtClass}>NQ management start</dt>
          <dd className={ddClass}>
            {new Date(content.trackRecordStartedAt).toLocaleDateString()}
          </dd>
        </div>
      )}
      {trackRecord && (
        <div className={rowClass}>
          <dt className={dtClass}>Track record</dt>
          <dd className={ddClass}>{trackRecord}</dd>
        </div>
      )}
      <div className={rowClass}>
        <dt className={dtClass}>Last rebalanced</dt>
        <dd className={ddClass}>{formatMaybeDateTime(account.lastRebalancedAt ?? undefined)}</dd>
      </div>
      <div className={rowClass}>
        <dt className={dtClass}>Created</dt>
        <dd className={ddClass}>{new Date(account.createdAt).toLocaleDateString()}</dd>
      </div>
    </dl>
  );

  return (
    <AccountCard
      title={title}
      accountType={accountType}
      statuses={[
        {
          // A deactivated live-money account is not "live": say so on the pill
          // (paper stays "Paper"; its dot still carries the active state).
          label: account.isPaper ? 'Paper' : account.active ? 'Live' : 'Not live',
          variant: accountType,
          activeDot: account.active,
          title: account.active ? 'Account is active' : 'Account is inactive',
        },
      ]}
      actions={actions}
    >
      {contentError && (
        <div className="mb-3 rounded-[8px] border border-destructive/40 bg-destructive/10 p-3 text-center text-[14px] text-destructive">
          {contentError}
        </div>
      )}
      <SharedAccountBody
        totalValue={content?.totalValue}
        cash={content?.cash}
        invested={content?.stockValue}
        performance={performance}
        positions={positions}
        positionsLoaded={content !== undefined}
        positionsLoading={contentLoading}
        positionsError={contentError}
        onLoadPositionsIfNeeded={onLoadContentIfNeeded}
        details={details}
        controls={controls}
      />
    </AccountCard>
  );
};

export default RetailAccountCard;
